import React, { useEffect, useMemo, useState } from "react";

import { Container } from "./styles";
import {
  Shell,
  Header,
  Title,
  Desc,
  FormGrid,
  GroupTitle,
  Field,
  Label,
  Hint,
  Select,
  Input,
  NumberInput,
  PillRow,
  Pill,
  RowEnd,
  PrimaryButton,
  GhostButton,
  ErrorBox,
  SkeletonGrid,
  Toggle,
  ToggleKnob,
} from "./styles";
import { apiKVMInstanceCreate, kvmAPI } from "api/kvm";
import { useTaskStore } from "store/task";
import { useHeader } from "components/header/store";

const CreatePage = () => {
  const { setTitle, setButtons } = useHeader();
  useEffect(() => {
    setTitle("CREATE");
    setButtons(null);
  }, [setTitle, setButtons]);
  const { addTask } = useTaskStore();

  // 로딩 / 에러
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // 원본 리스트 (문자열 배열 가정)
  const [hypervisors, setHypervisors] = useState<string[]>([]);
  const [networks, setNetworks] = useState<string[]>([]);
  const [flavors, setFlavors] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [kinds] = useState<string[]>(["vdi", "server", "renderfarm"]);

  // 폼 상태
  const [instanceName, setInstanceName] = useState("");
  const [image, setImage] = useState("");
  const [flavor, setFlavor] = useState("");
  const [tenantNetwork, setTenantNetwork] = useState("");
  const [storageNetwork, setStorageNetwork] = useState("");
  const [size, setSize] = useState<number | "">("");
  const [hostname, setHostname] = useState("");
  const [kind, setKind] = useState("");
  const [ouname, setOuname] = useState("");
  const [username, setUsername] = useState("");
  const [cloudConfig, setCloudConfig] = useState(true);

  // 초기 데이터 로드
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const [hv, nw, fl, im] = await Promise.all([
          (await kvmAPI.hypervisor()).data?.data ?? [],
          (await kvmAPI.network()).data?.data ?? [],
          (await kvmAPI.flavors()).data?.data ?? [],
          (await kvmAPI.images()).data?.data ?? [],
        ]);

        if (!alive) return;
        setHypervisors(hv);
        setNetworks(nw);
        setFlavors(fl);
        setImages(im);

        // 기본값 자동 선택
        setHostname((prev) => prev || "");
        setTenantNetwork((prev) => prev || nw[0] || "");
        setStorageNetwork((prev) => prev || "");
        setFlavor((prev) => prev || fl[0] || "");
        setImage((prev) => prev || im[0] || "");
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message ?? "데이터를 불러오지 못했습니다.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // 생성 가능 여부
  const canCreate = useMemo(() => {
    const required = instanceName.trim() && image && flavor && tenantNetwork;
    const sizeOk = size !== "" && Number(size) > 0;
    return Boolean(required && sizeOk);
  }, [instanceName, image, flavor, tenantNetwork, size]);

  const onReset = () => {
    setInstanceName("");
    setImage(images[0] || "");
    setFlavor(flavors[0] || "");
    setTenantNetwork(networks[0] || "");
    setStorageNetwork(networks[1] || networks[0] || "");
    setSize("");
    setHostname("");
    setKind("");
    setOuname("");
    setUsername("");
    setCloudConfig(true);
  };

  const onCreate = async () => {
    const data = await apiKVMInstanceCreate(
      instanceName,
      image,
      flavor.match(/^\S+/)?.[0] ?? "",
      tenantNetwork.trim().split(/\s+/)[0],
      storageNetwork.trim().split(/\s+/)[0],
      size,
      hostname,
      kind,
      ouname,
      username,
      cloudConfig
    );

    addTask({
      name: instanceName,
      id: data.data.task_id,
      job: "VDI Create",
      state: "pending",
    });
    onReset();
  };

  return (
    <Container>
      <Shell $accent="indigo">
        <Header>
          <Title>VDI 생성</Title>
          <Desc>새 인스턴스를 만들기 위한 필수 정보와 옵션을 설정하세요.</Desc>
        </Header>

        {loading ? (
          <SkeletonGrid>
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="skeleton" key={i} />
            ))}
          </SkeletonGrid>
        ) : err ? (
          <ErrorBox>에러: {err}</ErrorBox>
        ) : (
          <>
            {/* 인스턴스 */}
            <GroupTitle>Instance</GroupTitle>
            <FormGrid>
              <Field>
                <Label htmlFor="instanceName">Instance Name</Label>
                <Input
                  id="instanceName"
                  placeholder="예: oasys-0101"
                  maxLength={64}
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value)}
                />
                <Hint>생성할 VDI의 이름을 합니다.</Hint>
              </Field>

              <Field>
                <Label htmlFor="hostname">Host (Hypervisor)</Label>
                <Select
                  id="hostname"
                  value={hostname}
                  onChange={(e) => setHostname(e.target.value)}
                >
                  <option value="">{`(선택 안 함 / 자동)`}</option>
                  {hypervisors.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
                <Hint>할당할 하이퍼바이저를 선택합니다.</Hint>
              </Field>
            </FormGrid>

            {/* 이미지 / 플레이버 */}
            <GroupTitle>Image & Flavor</GroupTitle>
            <FormGrid>
              <Field>
                <Label htmlFor="image">Image</Label>

                <PillRow role="listbox" aria-label="Flavor select">
                  {images.map((v) => (
                    <Pill
                      key={v}
                      role="option"
                      aria-selected={image === v}
                      $active={image === v}
                      onClick={() => setImage(v)}
                      title={v}
                    >
                      {v}
                    </Pill>
                  ))}
                </PillRow>
                <Hint>사용할 OS 이미지를 선택합니다.</Hint>
              </Field>

              <Field>
                <Label htmlFor="Flavor">Flavor</Label>
                <Select
                  id="Flavor"
                  value={flavor}
                  onChange={(e) => setFlavor(e.target.value)}
                >
                  {flavors.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
                <Hint>사양 프리셋을 선택합니다.</Hint>
              </Field>
            </FormGrid>

            {/* 네트워킹 */}
            <GroupTitle>Networking</GroupTitle>
            <FormGrid>
              <Field>
                <Label htmlFor="tenant_network">Tenant Network</Label>
                <Select
                  id="tenant_network"
                  value={tenantNetwork}
                  onChange={(e) => setTenantNetwork(e.target.value)}
                >
                  {networks.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
                <Hint>VDI 트래픽이 흐를 기본 네트워크입니다.</Hint>
              </Field>

              <Field>
                <Label htmlFor="storage_network">Storage Network</Label>
                <Select
                  id="storage_network"
                  value={storageNetwork}
                  onChange={(e) => setStorageNetwork(e.target.value)}
                >
                  {/* ✅ 선택 안함 */}
                  <option value="">(선택 안 함)</option>

                  {networks.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
                <Hint>
                  스토리지 IO를 위한 별도 네트워크입니다. (선택 안 하면 자동)
                </Hint>
              </Field>
            </FormGrid>

            {/* 크기 / 옵션 */}
            <GroupTitle>Size</GroupTitle>
            <FormGrid>
              <Field>
                <Label htmlFor="size">Size (GB)</Label>
                <NumberInput
                  id="size"
                  min={1}
                  step={1}
                  placeholder="예: 50"
                  value={size}
                  onChange={(e) =>
                    setSize(e.target.value === "" ? "" : Number(e.target.value))
                  }
                />
                <Hint>디스크 사이즈(숫자). 기본 40.</Hint>
              </Field>
            </FormGrid>

            {/* 메타 */}
            <GroupTitle>Meta</GroupTitle>

            <FormGrid>
              <Field>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="예: gildong-hong"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="ouname">OU Name</Label>
                <Input
                  id="ouname"
                  placeholder="조직 단위명"
                  value={ouname}
                  onChange={(e) => setOuname(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="kind">Kind</Label>

                <PillRow role="listbox" aria-label="kind select">
                  {kinds.map((v) => (
                    <Pill
                      key={v}
                      role="option"
                      aria-selected={kind === v}
                      $active={kind === v}
                      onClick={() => setKind(v)}
                      title={v}
                    >
                      {v}
                    </Pill>
                  ))}
                </PillRow>
              </Field>

              <Field>
                <Label htmlFor="cloud_config">Cloud Config</Label>
                <Toggle
                  id="cloud_config"
                  role="switch"
                  aria-checked={cloudConfig}
                  $on={cloudConfig}
                  onClick={() => setCloudConfig((s) => !s)}
                >
                  <ToggleKnob $on={cloudConfig} />
                </Toggle>
              </Field>
            </FormGrid>

            {/* 액션 */}
            <RowEnd>
              <GhostButton type="button" onClick={onReset}>
                초기화
              </GhostButton>
              <PrimaryButton
                type="button"
                onClick={onCreate}
                disabled={!canCreate}
                aria-disabled={!canCreate}
              >
                생성하기
              </PrimaryButton>
            </RowEnd>
          </>
        )}
      </Shell>
    </Container>
  );
};

export default CreatePage;
